<template>
  <!-- One entry in the /blog index. Deliberately unboxed: no border, no panel,
       no chips — the cover image is the card's edge, and everything below it is
       plain text on the page background. The whole thing is still a single
       anchor (the <span> overlay), so a crawler reads one link per post rather
       than three competing ones, the same way BrandCard does. -->
  <li
    class="group relative flex flex-col"
    :class="featured ? 'sm:col-span-2' : ''"
  >
    <div :class="featured ? 'sm:grid sm:grid-cols-2 sm:items-center sm:gap-10' : ''">
      <!-- The cover — the post's artwork, not its share card: the share card
           has the headline set into it, and the headline is already the next
           line on this card. Drawn at the 1200x630 the images actually are, so
           nothing is cropped, with explicit dimensions so the card never
           resizes when the image lands (the CLS problem BrandCard documents). Alt text stays empty unless the post supplies a real
           description: the headline directly below already carries the meaning,
           and the generated covers are decoration. -->
      <div
        v-if="cover"
        class="mb-5 overflow-hidden rounded-2xl bg-gray-900"
        :class="featured ? 'sm:mb-0' : ''"
      >
        <img
          :src="cover"
          :alt="post.imageAlt || ''"
          width="1200"
          height="630"
          :loading="eager ? 'eager' : 'lazy'"
          :fetchpriority="eager ? 'high' : 'auto'"
          decoding="async"
          class="aspect-[1200/630] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
        />
      </div>

      <div>
        <!-- Section, then date. Monospaced and letterspaced so the line reads
             as a label rather than as the first line of the text. -->
        <div class="flex flex-wrap items-center gap-x-2 font-mono text-xs uppercase tracking-widest">
          <span :style="{ color: post.categoryAccent }">{{ post.categoryName }}</span>
          <span class="text-gray-700" aria-hidden="true">/</span>
          <time :datetime="toDateAttr(post.date)" class="text-gray-500">{{ published }}</time>

          <!-- Drafts only ever reach a page in `nuxt dev` (server/utils/blog.ts
               drops them everywhere else), so this is a working aid: it marks
               the posts on screen that would not exist in production. -->
          <BaseBadge v-if="post.status === 'draft'" tone="warning" size="xs" class="tracking-wide">Draft</BaseBadge>
        </div>

        <component
          :is="headingTag"
          class="mt-3 font-semibold tracking-tight text-white transition group-hover:text-indigo-200"
          :class="featured ? 'text-2xl sm:text-3xl' : 'text-xl'"
        >
          <!-- The overlay makes the whole card the link's hit area, so it
               also carries the link's focus ring. -->
          <NuxtLink :to="post.path" class="group/link focus-visible:outline-none" @click="trackOpen">
            <span class="absolute inset-0 rounded-2xl group-focus-visible/link:outline group-focus-visible/link:outline-2 group-focus-visible/link:outline-offset-4 group-focus-visible/link:outline-indigo-500" aria-hidden="true"></span>
            {{ post.title }}
          </NuxtLink>
        </component>

        <p
          class="mt-3 leading-7 text-gray-400"
          :class="featured ? 'line-clamp-3 text-base' : 'line-clamp-3 text-sm leading-6'"
        >{{ post.excerpt }}</p>
      </div>
    </div>
  </li>
</template>

<script setup>
import { computed } from 'vue'
import { trackEvent } from '~/composables/analytics'
import BaseBadge from '~/components/atoms/BaseBadge.vue'
import { formatPostDateShort, toDateAttr } from '~/utils/postDate'

const props = defineProps({
  // A PostSummary from server/utils/blog.ts.
  post: { type: Object, required: true },
  // The lead card: spans the grid, cover beside the text, larger type.
  featured: { type: Boolean, default: false },
  // The index's own <h1> is the page title, so cards are h2 there. The
  // "further reading" list at the foot of an article sits under an <h2> of its
  // own and passes h3.
  headingTag: { type: String, default: 'h2' },
  // Set on the one card above the fold, so its cover isn't lazy-loaded (which
  // would make the largest element on the page wait for the whole document).
  eager: { type: Boolean, default: false },
})

// `cover` is the on-page artwork; older posts that only ever had one image
// fall back to it, which is what a single supplied picture means.
const cover = computed(() => props.post.cover || props.post.image)

const published = computed(() => formatPostDateShort(props.post.date))

// Which posts the index actually sends people to — the counterpart to the
// filter events, so a report can tell browsing from targeted arrival.
const trackOpen = () => {
  try {
    trackEvent('Blog Card Opened', { post: props.post.slug, featured: props.featured })
  } catch {}
}
</script>
